import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { MapContainer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Plus, Search, X } from "lucide-react";

import { Badge, Button, Chip, ChipRow, OptionPicker, Sheet } from "@/ui";
import { PLACE_COLOR, PLACE_COLOR_SOFT } from "@/lib/eventColors";
import { useCreateSpot, useSpots, useTournaments } from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";
import { SpotEditSheet } from "@/features/spots/SpotEditSheet";
import { api } from "@/lib/api";
import {
  AttributionBadge,
  BaseTiles,
  LocateButton,
  LocateErrorToast,
  SpotSearchBox,
  UserPositionMarker,
} from "@/features/map/shared";
import {
  LIVE_MARKER_ICON,
  MAP_ROUND_BUTTON,
  MARKER_ICONS,
  MOSCOW,
  searchSpots,
  useInvalidateOnViewport,
} from "@/features/map/mapUtils";
import { useUserLocation } from "@/features/map/useUserLocation";
import { noAutofill } from "@/lib/noAutofill";
import type { NetType, PlaceType, Spot, Tournament } from "@/types/api";

import "leaflet/dist/leaflet.css";

const PLACE_LABEL: Record<PlaceType, string> = {
  outdoor_spot: "Уличный",
  indoor_hall: "Зал",
  store: "Магазин",
};

// Dashed/translucent variant used while the user is placing a new spot —
// distinguishes it from real spots still on the map.
const DRAFT_MARKER_ICON: L.DivIcon = L.divIcon({
  className: "sp-marker sp-marker--draft",
  iconSize: [24, 32],
  iconAnchor: [12, 32],
  html: `
    <svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.3 0 11.8 0 20 12 32 12 32s12-12 12-20.2C24 5.3 18.6 0 12 0z" fill="#FF5A1F" fill-opacity="0.85" stroke="#fff" stroke-width="2" />
      <circle cx="12" cy="12" r="5" fill="#fff" />
    </svg>`,
});

type Filter = "all" | PlaceType;

export function MapPage() {
  const { user } = useAuth();
  const { data: spots = [] } = useSpots();
  const { data: tournaments = [] } = useTournaments();
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Spot | null>(null);
  const [editing, setEditing] = useState<Spot | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pickMode, setPickMode] = useState(false);
  const [draftPos, setDraftPos] = useState<L.LatLngTuple | null>(null);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [draftAddress, setDraftAddress] = useState<string>("");
  const [draftAddressLoading, setDraftAddressLoading] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Map spot_id → list of currently-running tournaments at that spot. Lets
  // us paint a "live" marker and surface running events in the spot sheet.
  const liveBySpot = useMemo(() => {
    const map = new Map<number, Tournament[]>();
    for (const t of tournaments) {
      if (t.status !== "in_progress") continue;
      const arr = map.get(t.spot_id) ?? [];
      arr.push(t);
      map.set(t.spot_id, arr);
    }
    return map;
  }, [tournaments]);

  useInvalidateOnViewport(mapRef);

  const {
    userPos,
    locating,
    locateError,
    fixStale,
    dismissError,
    handleLocateClick,
  } = useUserLocation({
    onFirstFix: (p) => mapRef.current?.flyTo([p.lat, p.lng], 15, { duration: 0.8 }),
    onRecenter: (p) => mapRef.current?.flyTo([p.lat, p.lng], 15, { duration: 0.8 }),
  });

  const filtered = useMemo(
    () => (filter === "all" ? spots : spots.filter((s) => s.place_type === filter)),
    [filter, spots]
  );

  // Search hits — names + addresses, case-insensitive, capped at 8.
  const searchResults = useMemo(() => searchSpots(spots, searchQuery), [searchQuery, spots]);

  const openSearch = () => {
    setSearchOpen(true);
    // Wait one frame so the input mounts before we focus it.
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
  };

  const pickSearchResult = (spot: Spot) => {
    closeSearch();
    setSelected(spot);
    if (mapRef.current) {
      mapRef.current.flyTo([spot.latitude, spot.longitude], 16, { duration: 0.7 });
    }
  };

  const startPickMode = () => {
    setPickMode(true);
    setDraftPos(null);
    setDraftAddress("");
    closeSearch();
    setSelected(null);
  };

  const cancelPickMode = () => {
    setPickMode(false);
    setDraftPos(null);
    setCreateSheetOpen(false);
    setDraftAddress("");
  };

  const handleMapPick = (pos: L.LatLngTuple) => {
    if (!pickMode) return;
    setDraftPos(pos);
    setCreateSheetOpen(true);
    // Try to reverse-geocode via our backend (Nominatim). Best-effort —
    // the form still lets the user edit/override the result.
    setDraftAddress("");
    setDraftAddressLoading(true);
    api
      .get<{ address?: string }>(`/spots/geocode?lat=${pos[0]}&lon=${pos[1]}`)
      .then((res) => {
        const addr = (res.address ?? "").trim();
        if (addr) setDraftAddress(addr);
      })
      .catch((err) => {
        // Non-blocking: user can type the address by hand.
        // eslint-disable-next-line no-console
        console.warn("reverse-geocode failed", err);
      })
      .finally(() => setDraftAddressLoading(false));
  };

  // Address-search path: user typed an address, picked a suggestion. We
  // already have the label from Nominatim, so skip the reverse-geocode round
  // trip and just drop the pin at the returned coordinates.
  const handleAddressPick = (lat: number, lng: number, label: string) => {
    if (!pickMode) return;
    const pos: L.LatLngTuple = [lat, lng];
    setDraftPos(pos);
    setDraftAddress(label);
    setDraftAddressLoading(false);
    setCreateSheetOpen(true);
    if (mapRef.current) {
      mapRef.current.flyTo(pos, 17, { duration: 0.8 });
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: 8,
          left: 0,
          right: 0,
          // Leaflet internal panes cap around 700 and its control container
          // sits at 1000 — keep the filter bar above everything the map draws.
          zIndex: 1100,
          pointerEvents: "none",
        }}
      >
        <div style={{ pointerEvents: "auto" }}>
          <ChipRow>
            <Chip active={filter === "all"} onClick={() => setFilter("all")}>
              Все
            </Chip>
            <Chip
              active={filter === "outdoor_spot"}
              color={PLACE_COLOR.outdoor_spot}
              colorSoft={PLACE_COLOR_SOFT.outdoor_spot}
              onClick={() => setFilter("outdoor_spot")}
            >
              Уличные
            </Chip>
            <Chip
              active={filter === "indoor_hall"}
              color={PLACE_COLOR.indoor_hall}
              colorSoft={PLACE_COLOR_SOFT.indoor_hall}
              onClick={() => setFilter("indoor_hall")}
            >
              Залы
            </Chip>
            <Chip
              active={filter === "store"}
              color={PLACE_COLOR.store}
              colorSoft={PLACE_COLOR_SOFT.store}
              onClick={() => setFilter("store")}
            >
              Магазины
            </Chip>
          </ChipRow>
        </div>
      </div>

      <MapContainer
        center={MOSCOW}
        zoom={11}
        zoomControl={false}
        attributionControl={false}
        maxZoom={20}
        ref={(m) => {
          mapRef.current = m;
        }}
        style={{
          height: "calc(100dvh - 56px - var(--safe-top) - var(--safe-bottom))",
          width: "100%",
          // New stacking context: Leaflet's panes (z-index up to 1000) would
          // otherwise paint over the tab bar's raised «Играть» button.
          isolation: "isolate",
        }}
      >
        <BaseTiles />
        {pickMode && <MapPickHandler onPick={handleMapPick} />}
        {draftPos && (
          <Marker position={draftPos} icon={DRAFT_MARKER_ICON} zIndexOffset={600} />
        )}
        {filtered.map((spot) => {
          const isLive = liveBySpot.has(spot.id);
          return (
            <Marker
              key={spot.id}
              position={[spot.latitude, spot.longitude]}
              icon={isLive ? LIVE_MARKER_ICON : MARKER_ICONS[spot.place_type]}
              zIndexOffset={isLive ? 500 : 0}
              eventHandlers={{ click: () => setSelected(spot) }}
            />
          );
        })}
        {userPos && <UserPositionMarker pos={userPos} />}
      </MapContainer>

      <div
        style={{
          position: "absolute",
          top: 56,
          right: 16,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 8,
          width: searchOpen ? "min(360px, calc(100% - 32px))" : "auto",
        }}
      >
        {searchOpen ? (
          <SpotSearchBox
            inputRef={searchInputRef}
            value={searchQuery}
            onChange={setSearchQuery}
            onClose={closeSearch}
            results={searchResults}
            onPick={pickSearchResult}
            isLive={(id) => liveBySpot.has(id)}
          />
        ) : (
          <button
            type="button"
            onClick={openSearch}
            aria-label="Найти локацию"
            title="Найти локацию"
            style={MAP_ROUND_BUTTON}
          >
            <Search size={20} />
          </button>
        )}
      </div>

      {locateError && (
        <LocateErrorToast message={locateError} onDismiss={dismissError} />
      )}

      {pickMode && !draftPos && (
        <PickModePanel
          onPickResult={handleAddressPick}
          onCancel={cancelPickMode}
        />
      )}

      <button
        type="button"
        onClick={pickMode ? cancelPickMode : startPickMode}
        aria-label={pickMode ? "Отменить добавление" : "Добавить площадку"}
        title={pickMode ? "Отменить" : "Добавить площадку"}
        style={{
          position: "absolute",
          right: 16,
          bottom: `calc(76px + var(--safe-bottom))`,
          zIndex: 1100,
          width: 48,
          height: 48,
          borderRadius: "50%",
          border: "none",
          background: pickMode ? "var(--fg-danger)" : "var(--brand-orange)",
          color: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        {pickMode ? <X size={22} /> : <Plus size={22} />}
      </button>

      <AttributionBadge />

      <LocateButton
        locating={locating}
        fixStale={fixStale}
        locateError={locateError}
        onClick={handleLocateClick}
        style={{
          position: "absolute",
          right: 16,
          bottom: `calc(16px + var(--safe-bottom))`,
          zIndex: 1100,
        }}
      />

      <Sheet open={!!selected} onClose={() => setSelected(null)} title={selected?.name}>
        {selected && (
          <SpotSheet
            spot={selected}
            liveTournaments={liveBySpot.get(selected.id) ?? []}
            canEdit={!!user && (user.is_admin || selected.created_by === user.id)}
            onEdit={() => {
              setEditing(selected);
              setSelected(null);
            }}
          />
        )}
      </Sheet>

      <SpotEditSheet spot={editing} onClose={() => setEditing(null)} />

      <Sheet
        open={createSheetOpen && draftPos != null}
        onClose={() => setCreateSheetOpen(false)}
        title="Новая площадка"
      >
        {draftPos && (
          <CreateSpotForm
            position={draftPos}
            canCreateHallOrStore={!!user?.is_admin}
            initialAddress={draftAddress}
            addressLoading={draftAddressLoading}
            onCancel={() => setCreateSheetOpen(false)}
            onCreated={() => {
              setCreateSheetOpen(false);
              setPickMode(false);
              setDraftPos(null);
              setDraftAddress("");
            }}
          />
        )}
      </Sheet>
    </div>
  );
}

function MapPickHandler({ onPick }: { onPick: (pos: L.LatLngTuple) => void }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

type GeocodeHit = {
  lat: number;
  lon: number;
  label: string;
  full_address: string;
};

function PickModePanel({
  onPickResult,
  onCancel,
}: {
  onPickResult: (lat: number, lng: number, label: string) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<GeocodeHit[]>([]);
  const [loading, setLoading] = useState(false);
  // Each request carries a sequence number so a slow earlier reply can't
  // overwrite a fast later one. AbortController would do the same but the
  // shared `api` helper doesn't expose signal forwarding.
  const reqSeqRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setHits([]);
      setLoading(false);
      return;
    }
    const mySeq = ++reqSeqRef.current;
    setLoading(true);
    const t = window.setTimeout(() => {
      api
        .get<GeocodeHit[]>(`/spots/geocode/search?q=${encodeURIComponent(q)}`)
        .then((res) => {
          if (reqSeqRef.current !== mySeq) return;
          setHits(Array.isArray(res) ? res : []);
        })
        .catch(() => {
          if (reqSeqRef.current !== mySeq) return;
          setHits([]);
        })
        .finally(() => {
          if (reqSeqRef.current !== mySeq) return;
          setLoading(false);
        });
    }, 300);
    return () => window.clearTimeout(t);
  }, [query]);

  return (
    <div
      role="region"
      aria-label="Добавить площадку"
      style={{
        position: "absolute",
        left: 16,
        right: 16,
        top: 56,
        zIndex: 1100,
        background: "var(--bg-section)",
        border: "1px solid var(--separator)",
        borderRadius: "var(--r-md)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
        }}
      >
        <span
          style={{
            flex: 1,
            fontFamily: "var(--font-body)",
            fontSize: 14,
            color: "var(--fg1)",
          }}
        >
          Введите адрес или тапните по карте
        </span>
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: "transparent",
            border: 0,
            color: "var(--brand-orange)",
            fontFamily: "var(--font-body)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Отмена
        </button>
      </div>
      <div style={{ padding: "0 12px 10px" }}>
        <input
          {...noAutofill}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="например, Аутлет Архангельское"
          aria-label="Поиск адреса"
          style={{
            width: "100%",
            padding: "8px 10px",
            fontFamily: "var(--font-body)",
            fontSize: 14,
            color: "var(--fg1)",
            background: "var(--bg-primary)",
            border: "1px solid var(--separator)",
            borderRadius: "var(--r-sm)",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>
      {(loading || hits.length > 0 || (query.trim().length >= 3 && !loading)) && (
        <div
          style={{
            borderTop: "1px solid var(--separator)",
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {loading && (
            <div
              style={{
                padding: "10px 12px",
                fontSize: 13,
                color: "var(--fg2)",
                fontFamily: "var(--font-body)",
              }}
            >
              Ищем…
            </div>
          )}
          {!loading && hits.length === 0 && query.trim().length >= 3 && (
            <div
              style={{
                padding: "10px 12px",
                fontSize: 13,
                color: "var(--fg2)",
                fontFamily: "var(--font-body)",
              }}
            >
              Ничего не нашлось. Можно тапнуть по карте.
            </div>
          )}
          {!loading &&
            hits.map((h, i) => (
              <button
                key={`${h.lat},${h.lon},${i}`}
                type="button"
                onClick={() => onPickResult(h.lat, h.lon, h.label)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  background: "transparent",
                  border: 0,
                  borderTop: i === 0 ? 0 : "1px solid var(--separator)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  color: "var(--fg1)",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 500 }}>{h.label}</div>
                {h.full_address && h.full_address !== h.label && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--fg2)",
                      marginTop: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {h.full_address}
                  </div>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function CreateSpotForm({
  position,
  canCreateHallOrStore,
  initialAddress,
  addressLoading,
  onCancel,
  onCreated,
}: {
  position: L.LatLngTuple;
  canCreateHallOrStore: boolean;
  initialAddress: string;
  addressLoading: boolean;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const createMut = useCreateSpot();
  const [name, setName] = useState("");
  const [address, setAddress] = useState(initialAddress);
  const [addressTouched, setAddressTouched] = useState(false);
  const [placeType, setPlaceType] = useState<PlaceType>("outdoor_spot");
  const [netType, setNetType] = useState<NetType>("metal");
  const [hasRoof, setHasRoof] = useState(false);
  const [tablesCount, setTablesCount] = useState("1");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");

  // Fill the address when the geocode response arrives — unless the user
  // already started typing something themselves.
  useEffect(() => {
    if (addressTouched) return;
    setAddress(initialAddress);
  }, [initialAddress, addressTouched]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await createMut.mutateAsync({
        name: name.trim(),
        address: address.trim(),
        latitude: position[0],
        longitude: position[1],
        place_type: placeType,
        net_type: placeType === "outdoor_spot" ? netType : null,
        has_roof: placeType === "outdoor_spot" ? hasRoof : null,
        tables_count: placeType === "outdoor_spot" ? Number(tablesCount) : null,
        website: website.trim() || null,
        phone: phone.trim() || null,
      });
      onCreated();
    } catch {
      /* error surfaced below */
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      style={{
        padding: "0 16px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <FormField label="Название">
        <TextInput value={name} onChange={setName} required />
      </FormField>
      <FormField label={addressLoading ? "Адрес (определяем…)" : "Адрес"}>
        <TextInput
          value={address}
          onChange={(v) => {
            setAddress(v);
            setAddressTouched(true);
          }}
          required
        />
      </FormField>
      <FormField label="Тип">
        <OptionPicker<PlaceType>
          value={placeType}
          onChange={setPlaceType}
          options={[
            { value: "outdoor_spot", label: "Уличная площадка" },
            ...(canCreateHallOrStore
              ? ([
                  { value: "indoor_hall" as PlaceType, label: "Зал" },
                  { value: "store" as PlaceType, label: "Магазин" },
                ] as const)
              : []),
          ]}
        />
      </FormField>

      {placeType === "outdoor_spot" && (
        <>
          <FormField label="Сетка">
            <OptionPicker<NetType>
              value={netType}
              onChange={setNetType}
              options={[
                { value: "metal", label: "Металлическая" },
                { value: "regular", label: "Обычная" },
                { value: "none", label: "Нет сетки" },
              ]}
            />
          </FormField>
          <ToggleRow
            label="Есть навес"
            checked={hasRoof}
            onChange={setHasRoof}
          />
          <FormField label="Количество столов">
            <TextInput
              type="number"
              value={tablesCount}
              onChange={setTablesCount}
              min={1}
              max={50}
            />
          </FormField>
        </>
      )}

      {placeType !== "outdoor_spot" && (
        <>
          <FormField label="Сайт">
            <TextInput value={website} onChange={setWebsite} type="url" />
          </FormField>
          <FormField label="Телефон">
            <TextInput value={phone} onChange={setPhone} type="tel" />
          </FormField>
        </>
      )}

      <div
        style={{
          fontSize: 13,
          color: "var(--fg2)",
          background: "var(--bg-secondary)",
          padding: 10,
          borderRadius: "var(--r-md)",
          fontFamily: "var(--font-body)",
        }}
      >
        Координаты:{" "}
        <code style={{ fontFamily: "var(--font-mono)" }}>
          {position[0].toFixed(5)}, {position[1].toFixed(5)}
        </code>
      </div>

      {createMut.error && (
        <div
          style={{
            background: "rgba(223,63,64,0.1)",
            color: "var(--fg-danger)",
            padding: "8px 12px",
            borderRadius: "var(--r-md)",
            fontSize: 13,
          }}
        >
          {(createMut.error as Error).message}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Button type="submit" disabled={createMut.isPending || !name || !address}>
          {createMut.isPending ? "Создаём…" : "Создать"}
        </Button>
        <Button variant="ghost" onClick={onCancel}>Отмена</Button>
      </div>
    </form>
  );
}

const sheetInputStyle: React.CSSProperties = {
  border: "1px solid var(--separator)",
  borderRadius: "var(--r-md)",
  padding: "10px 14px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  background: "var(--bg)",
  color: "var(--fg1)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg2)" }}>{label}</span>
      {children}
    </div>
  );
}

function TextInput({
  value, onChange, type = "text", min, max, required,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  min?: number;
  max?: number;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      min={min}
      max={max}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      style={sheetInputStyle}
      {...noAutofill}
    />
  );
}

function SpotSheet({
  spot,
  liveTournaments,
  canEdit,
  onEdit,
}: {
  spot: Spot;
  liveTournaments: Tournament[];
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {liveTournaments.length > 0 && <Badge kind="live">Идёт турнир</Badge>}
        <Badge kind="brand">{PLACE_LABEL[spot.place_type]}</Badge>
        {spot.place_type === "outdoor_spot" && spot.has_roof != null && (
          <Badge kind="neutral">{spot.has_roof ? "С навесом" : "Без навеса"}</Badge>
        )}
        {spot.tables_count != null && (
          <Badge kind="neutral">{spot.tables_count} стол{plural(spot.tables_count)}</Badge>
        )}
      </div>
      {liveTournaments.length > 0 && (
        <div
          style={{
            background: "var(--brand-orange-ghost)",
            color: "var(--brand-orange)",
            border: "1px solid rgba(255,90,31,0.25)",
            borderRadius: "var(--r-md)",
            padding: "10px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {liveTournaments.map((t) => (
            <div key={t.id} style={{ fontSize: 14, fontWeight: 600 }}>
              🔴 {t.name}
            </div>
          ))}
        </div>
      )}
      <div className="body" style={{ color: "var(--fg2)" }}>{spot.address}</div>
      {spot.phone && (
        <div className="body">
          Телефон: <a href={`tel:${spot.phone}`}>{spot.phone}</a>
        </div>
      )}
      {spot.website && (
        <div className="body">
          Сайт: <a href={spot.website} target="_blank" rel="noreferrer">{spot.website}</a>
        </div>
      )}
      <a
        href={`https://yandex.ru/maps/?rtext=~${spot.latitude},${spot.longitude}`}
        target="_blank"
        rel="noreferrer"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "12px 20px",
          background: "var(--brand-orange)",
          color: "#fff",
          fontWeight: 600,
          borderRadius: 999,
          textDecoration: "none",
        }}
      >
        Построить маршрут
      </a>
      {canEdit && (
        <Button variant="secondary" fullWidth onClick={onEdit}>
          Редактировать / удалить
        </Button>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        textAlign: "left",
        background: "var(--bg)",
        border: "1px solid var(--separator)",
        borderRadius: "var(--r-md)",
        padding: "10px 14px",
        color: "var(--fg1)",
        cursor: "pointer",
        fontFamily: "var(--font-body)",
        fontSize: 15,
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 24,
          borderRadius: 999,
          background: checked ? "var(--brand-orange)" : "var(--bg-secondary)",
          border: "1px solid var(--separator)",
          position: "relative",
          transition: "background var(--d-fast, 150ms) ease",
          flex: "none",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 1,
            left: checked ? 17 : 1,
            width: 20,
            height: 20,
            borderRadius: 999,
            background: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
            transition: "left var(--d-fast, 150ms) ease",
          }}
        />
      </span>
    </button>
  );
}

function plural(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "ов";
  if (mod10 === 1) return "";
  if (mod10 >= 2 && mod10 <= 4) return "а";
  return "ов";
}
